import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Card } from '@w7/ui'
import { bookmakerApi, bmErrorMessage, unwrapBmList } from '@/api/bookmaker'
import { getLabAdminLevel } from '@/config/lab'
import { BmPage } from './BmPage'
import { buildCompetitionCreatePayload } from './competitionCreatePayload'
import './create-forms.css'

type GameOpt = Record<string, unknown>

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

function firstEntity(data: unknown): Record<string, unknown> {
  if (Array.isArray(data)) return asRecord(data[0])
  const o = asRecord(data)
  if (Array.isArray(o.result)) return asRecord(o.result[0])
  return o
}

export function CreateCompPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [games, setGames] = useState<GameOpt[]>([])
  const [name, setName] = useState('')
  const [gameKey, setGameKey] = useState('')
  const [entity, setEntity] = useState<Record<string, unknown>>({})

  useEffect(() => {
    void (async () => {
      try {
        const data = await bookmakerApi.gameList({})
        let list = unwrapBmList(data)
        if (getLabAdminLevel() === 'Horse Racing') {
          list = list.filter((g) => String(g.name) === 'Horse Racing')
        }
        setGames(list)
      } catch (e) {
        setError(bmErrorMessage(e))
      }
    })()
  }, [])

  useEffect(() => {
    if (!id) return
    void (async () => {
      setBusy(true)
      try {
        const row = firstEntity(await bookmakerApi.competitionGetOne({ id }))
        setEntity(row)
        setName(String(row.name ?? ''))
        setGameKey(String(row.game ?? row.eventTypeId ?? ''))
      } catch (e) {
        setError(bmErrorMessage(e))
      } finally {
        setBusy(false)
      }
    })()
  }, [id])

  async function onSubmit() {
    if (!name.trim()) {
      setError('Insert Valid Name')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (isEdit) {
        await bookmakerApi.competitionEdit({
          ...entity,
          _id: id,
          id: entity.id ?? id,
          name: name.trim(),
          status: entity.status,
        })
      } else {
        const game = games.find(
          (g) => String(g._id) === gameKey || String(g.id) === gameKey || JSON.stringify(g) === gameKey,
        )
        if (!game) {
          setError('Select Game')
          setBusy(false)
          return
        }
        // Lab CreateCompCtrl: { name, game: game._id, betfairId: game.id }
        await bookmakerApi.competitionCreate(buildCompetitionCreatePayload(game, name))
      }
      navigate('/complist')
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BmPage
      title={isEdit ? 'Edit Competition' : 'Create Competition'}
      description="Lab create competition — API assigns a new 9-digit competition id"
      error={error}
    >
      <Card>
        <div className="bm-create">
          <div className="bm-create-row">
            <label htmlFor="bm-comp-name">Competition Name</label>
            <input
              id="bm-comp-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Insert Competition Name"
            />
          </div>
          <div className="bm-create-row">
            <label htmlFor="bm-comp-game">Game Name</label>
            <select
              id="bm-comp-game"
              value={gameKey}
              disabled={isEdit}
              onChange={(e) => setGameKey(e.target.value)}
            >
              <option value="">Select…</option>
              {games.map((g) => (
                <option key={String(g._id ?? g.id)} value={String(g._id ?? g.id)}>
                  {String(g.name ?? '')}
                </option>
              ))}
            </select>
          </div>
          <div className="bm-create-actions">
            <Button type="button" disabled={busy} onClick={() => void onSubmit()}>
              {busy ? 'Working…' : 'Submit'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/complist')}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </BmPage>
  )
}
